import { AuthApiService, NavigationService, OidcService } from '@revenuewell/front-end-bundle';
import { orderBy } from 'lodash';
import { stringify } from 'query-string';
import { dedupeContacts } from '../../utils/contact';
import {
  InboxCommunicationFilterRequest,
  PatientConnect365Enrollment,
  PatientNote,
  SecureAttachment,
  TreatmentPlanModel,
  TreatmentPlanStatus,
  UpdateTreatmentPlanItemModel,
  UpdateTreatmentPlanModel
} from './contact-api.types';
import { IContactProfile } from './interface';

interface IContactSearchParams {
  pattern: string;
  pageSize?: string;
  pageNumber?: string;
  sortBy: 'firstName' | 'lastName';
  sortOrder: 'asc' | 'desc';
}

export class ContactApi extends AuthApiService {
  private static instance: ContactApi;
  private apiUrl: string;

  constructor(config: any, oidcService: OidcService) {
    super(oidcService, new NavigationService());
    this.apiUrl = config.contact.apiUrl;
    return this;
  }

  public static initialize(config: any, oidcService: OidcService) {
    if (!ContactApi.instance) ContactApi.instance = new ContactApi(config, oidcService);
    return ContactApi.instance;
  }

  public static getInstance() {
    if (!ContactApi.instance) throw new Error('ContactApi should be initialized before first use.');
    return this.instance;
  }

  public async getContacts({
    searchText = '',
    limit,
    sortBy,
    pageNumber = 1,
    locationIds = ''
  }: ContactSearchParameters) {
    const result = await this.masterAccountGet<DataWrapper<Contact[]>>({
      pattern: searchText,
      sortBy,
      pageNumber,
      pageSize: limit,
      locationIds
    });
    return result;
  }

  public async searchContacts(
    params: IContactSearchParams
  ): Promise<{ total: number; totalLoaded: number; data: IContactProfile[] }> {
    const { masterAccountId } = await this.oidcService.getClaims();
    let url = `${this.apiUrl}/master-accounts/${masterAccountId}/contacts?`;
    url += new URLSearchParams(params as any).toString();

    const response = await this.authFetch(url.toString());
    const responseJson = await response.json();
    const data = responseJson.data as IContactProfile[];

    const dedupedData = dedupeContacts(data).map((contact) => ({
      ...contact,
      phoneNo: contact.cellPhone || contact.homePhone
    }));

    return {
      total: responseJson.total,
      totalLoaded: data.length,
      data: orderBy(dedupedData, ['isResponsibleParty', 'firstName', 'lastName'], ['desc', 'asc', 'asc'])
    };
  }

  private async masterAccountGet<T>(query: Record<string, string | number>) {
    const { masterAccountId } = await this.oidcService.getClaims();
    return await this.get<T>(`${this.apiUrl}/master-accounts/${masterAccountId}/contacts`, query);
  }

  private async get<T>(baseUrl: string, query: Record<string, string | number | string[]>) {
    const url = `${baseUrl}?${stringify(query)}`;
    const result = await this.authFetch(url);
    return result.json() as T;
  }

  private async masterAccountGetByPhoneNumbers<T>(query: Record<string, string | string[]>) {
    const { masterAccountId } = await this.oidcService.getClaims();
    return await this.get<T>(`${this.apiUrl}/master-accounts/${masterAccountId}/contacts-by-phones`, query);
  }

  public async getContactsByPhoneNumbers(phones: string | string[]) {
    const result = await this.masterAccountGetByPhoneNumbers<Record<string, ContactSearchByNumber[]>>({ phones });
    return result;
  }

  public async getIdenticalContacts(contactId: number) {
    const result = await this.authFetch(`${this.apiUrl}/contacts/${contactId}/identical-contacts`);
    return result.json() as ContactLocationInfo[];
  }

  public async getContactDetailsById(contactId: number) {
    const result = await this.authFetch(`${this.apiUrl}/contacts/${contactId}`);
    return result.json() as Contact;
  }

  public async getPatientInfoById(contactId: number) {
    const result = await this.authFetch(`${this.apiUrl}/contacts/${contactId}/patientInfo`);
    return result.json() as PatientInfo;
  }

  public async getPatientPreferences(contactId: number) {
    const result = await this.authFetch(`${this.apiUrl}/patient-preferences/${contactId}`);
    return result.json() as PatientPreferencesResponse;
  }

  public async patchPatientPreferences(
    contactId: number,
    patientPreferences: PatientPreferencesRequest
  ): Promise<PatientPreferencesResponse> {
    const result = await this.authFetch(`${this.apiUrl}/patient-preferences/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(patientPreferences),
      headers: { 'Content-Type': 'application/json' }
    });
    return result.json() as PatientPreferencesResponse;
  }

  public async getNotes(patientId: number, locationId: number): Promise<PatientNote[]> {
    const result = await this.authFetch(`${this.apiUrl}/contacts/${patientId}/notes?locationId=${locationId}`);
    return result.json();
  }

  public async createNote(patientId: number, locationId: number, text: string, authorId: number): Promise<void> {
    await this.authFetch(`${this.apiUrl}/contacts/${patientId}/notes?locationId=${locationId}`, {
      method: 'POST',
      body: JSON.stringify({ text, authorId }),
      headers: { 'Content-Type': 'application/json' }
    });
  }

  public async updateNote(noteId: number, locationId: number, text: string, authorId?: number): Promise<void> {
    await this.authFetch(`${this.apiUrl}/contacts/notes/${noteId}?locationId=${locationId}`, {
      method: 'PUT',
      body: JSON.stringify({ text, authorId }),
      headers: { 'Content-Type': 'application/json' }
    });
  }

  public async deleteNote(noteId: number, locationId: number): Promise<void> {
    await this.authFetch(`${this.apiUrl}/contacts/notes/${noteId}?locationId=${locationId}`, {
      method: 'DELETE'
    });
  }

  public async getTreatmentPlans(patientId: number, locationId: number): Promise<TreatmentPlanModel[]> {
    const result = await this.authFetch(
      `${this.apiUrl}/contacts/${patientId}/treatment-plans?locationId=${locationId}`
    );
    return result.json();
  }

  public async updateTreatmentPlan(
    patientId: number,
    locationId: number,
    planId: number,
    model: UpdateTreatmentPlanModel
  ): Promise<void> {
    await this.authFetch(`${this.apiUrl}/contacts/${patientId}/treatment-plan/${planId}?locationId=${locationId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(model)
    });
  }

  public async updateTreatmentPlanItem(
    patientId: number,
    locationId: number,
    planId: number,
    itemId: number,
    model: UpdateTreatmentPlanItemModel
  ): Promise<void> {
    await this.authFetch(
      `${this.apiUrl}/contacts/${patientId}/treatment-plan/${planId}/item/${itemId}?locationId=${locationId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(model)
      }
    );
  }

  public async getScheduledCommunications(patientId: number, locationId: number): Promise<Communication[]> {
    const response = await this.authFetch(
      `${this.apiUrl}/contacts/${patientId}/scheduled-communications?locationId=${locationId}`
    );
    return response.json();
  }

  public async getInboxManagedCommunications(
    locationId: number,
    request: InboxCommunicationFilterRequest
  ): Promise<InboxManagedCommunication[]> {
    const response = await this.authFetch(
      `${this.apiUrl}/contacts/inbox-managed-communications?locationId=${locationId}`,
      {
        method: 'POST',
        body: JSON.stringify(request),
        headers: { 'Content-Type': 'application/json' }
      }
    );
    const comms = await response.json();
    return comms.map((item: any) => ({
      ...item,
      timetoken: new Date(item.date).getTime()
    }));
  }

  public async getSecureAttachments(patientId: number, locationId: number): Promise<SecureAttachment[]> {
    const response = await this.authFetch(
      `${this.apiUrl}/contacts/${patientId}/secure-attachments?locationId=${locationId}`
    );
    return response.json();
  }

  public async getSecureAttachment(
    patientId: number,
    locationId: number,
    fileName: string
  ): Promise<{ downloadUrl: string }> {
    const response = await this.authFetch(
      `${this.apiUrl}/contacts/${patientId}/secure-attachments/${fileName}?locationId=${locationId}`
    );
    return response.json();
  }

  public async createSecureAttachment(patientId: number, locationId: number, payload: FormData): Promise<void> {
    await this.authFetch(`${this.apiUrl}/contacts/${patientId}/secure-attachments?locationId=${locationId}`, {
      method: 'POST',
      body: payload
    });
  }

  public async updateSecureAttachment(
    patientId: number,
    locationId: number,
    fileName: string,
    payload: FormData
  ): Promise<void> {
    await this.authFetch(
      `${this.apiUrl}/contacts/${patientId}/secure-attachments/${fileName}?locationId=${locationId}`,
      {
        method: 'PUT',
        body: payload
      }
    );
  }

  public async deleteSecureAttachment(patientId: number, locationId: number, fileName: string): Promise<void> {
    await this.authFetch(
      `${this.apiUrl}/contacts/${patientId}/secure-attachments/${fileName}?locationId=${locationId}`,
      {
        method: 'DELETE'
      }
    );
  }

  async getPatientConnect365Enrollment(patientId: number, locationId: number): Promise<PatientConnect365Enrollment> {
    const response = await this.authFetch(
      `${this.apiUrl}/contacts/${patientId}/patientConnectEnrollment?locationId=${locationId}`
    );

    return response.json();
  }

  async sendPatientConnect365Invitation(patientId: number, locationId: number) {
    await this.authFetch(`${this.apiUrl}/contacts/${patientId}/patient-connect-invite?locationId=${locationId}`, {
      method: 'POST'
    });
  }
}

export interface ContactSearchParameters {
  searchText?: string;
  pageNumber?: number;
  limit: number;
  sortBy: string;
  locationIds?: string;
}

export interface DataWrapper<T> {
  data: T;
  total: number;
}

export interface Contact {
  id: number;
  firstName: string;
  lastName: string;
  cellPhone?: string;
  homePhone?: string;
  workPhone?: string;
  email?: string;
  isEmailValid: boolean;
  address: Address;
  dateOfBirth?: string;
  location?: Location;
  gender: string;
  isResponsibleParty?: boolean;
  networkId: number;
  foreignId: string;
  isPatientPreferencesAvailable?: boolean;
}

export interface ContactSearchByNumber {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  locationId?: number;
  networkId: number;
  isResponsibleParty?: boolean;
  responsiblePartyId: number;
  cellPhone?: string;
  homePhone?: string;
  workPhone?: string;
}

export interface Address {
  address1: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface Location {
  id: number;
  name: string;
  address: Address;
}

export interface ContactLocationInfo {
  id: number;
  locationId: number;
  locationName?: string;
  networkId: number;
}

export interface PatientInfo {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  familyMembers?: Contact[];
  patientAlerts?: PatientAlert[];
  visits?: Visit[];
  nextAppointment: Visit;
  lastAppointment: Visit;
  recallDate?: string;
  insurance?: string;
  primBenefitsRemaining?: number;
  outstandingBalance30Days?: number;
  outstandingBalance60Days?: number;
  outstandingBalance90Days?: number;
  treatmentPlans?: TreatmentPlan[];
  communicationHistory?: Communication[];
  phoneNumbers: {
    cellPhone?: string;
    homePhone?: string;
    workPhone?: string;
  };
  insuranceInfo?: IInsuranceInfo;
  secondaryInsuranceInfo?: IInsuranceInfo;
}

export interface IInsuranceInfo {
  subscriberLastName: string;
  subscriberFirstName: string;
  subscriberDateOfBirth: Date;
  subscriberPostalCode: string;
  carrierId: string;
  groupId: string;
  memberId: string;
  beginningMonth: number;
  relation: number;
  employerName: string;
}

export interface Visit {
  date: string;
  notes?: string;
  provider?: string;
  location: Location;
  status: AppointmentStatus;
  amount: number;
}

export enum AppointmentStatus {
  Unconfirmed = 0,
  Confirmed = 1,
  Cancelled = 2,
  Deleted = 255
}

export interface PatientAlert {
  patientId: number;
  locationId?: number;
  firstName?: string;
  lastName?: string;
  alert?: string;
}

export interface TreatmentPlan {
  id: number;
  description: string;
  status: TreatmentPlanStatus;
  created: string;
  isInFollowUp: boolean;
}

export interface Communication {
  type: number;
  status: number;
  statusString: string;
  date: string;
  note: string;
  senderLocation: Location;
}

export interface InboxManagedCommunication {
  type: number;
  status: number;
  statusString: string;
  date: string;
  note: string;
  timetoken: number;
}

export interface PatientPreferencesResponse {
  patientId: number;
  emailAppointmentReminders: boolean;
  emailBirthdayAndHolidayCards: boolean;
  emailExpiringInsuranceReminder: boolean;
  emailPatientReactivation: boolean;
  emailPostOpInstructions: boolean;
  emailRecalls: boolean;
  emailReviewsAndSurveys: boolean;
  emailSpecialsAndNewsletters: boolean;
  emailTreatmentPlanFollowUps: boolean;
  dmAppointmentReminders: boolean;
  dmBirthdayAndHolidayCards: boolean;
  dmExpiringInsuranceReminder: boolean;
  dmPatientReactivation: boolean;
  dmRecalls: boolean;
  dmSpecialsAndNewsletters: boolean;
  dmTreatmentPlanFollowUps: boolean;
  textAppointmentReminders: boolean;
  textPatientReactivation: boolean;
  textRecalls: boolean;
  textReviewsAndSurveys: boolean;
  textSpecialsAndNewsletters: boolean;
  textTreatmentPlanFollowUps: boolean;
  pcAppointmentConfirmations: boolean;
  language: PatientLanguage;
  firstReminderScheduleStatus: PatientCustomReminderSchedule;
  secondReminderScheduleStatus: PatientCustomReminderSchedule;
  saveDateReminderScheduleStatus: PatientCustomReminderSchedule;
  postCardReminderScheduleStatus: PatientCustomReminderSchedule;
  sameDayReminderScheduleStatus: PatientCustomReminderSchedule;
  familyCommunicationReceiverId: number;
}

export interface PatientPreferencesRequest {
  emailAppointmentReminders?: boolean;
  emailBirthdayAndHolidayCards?: boolean;
  emailExpiringInsuranceReminder?: boolean;
  emailPatientReactivation?: boolean;
  emailPostOpInstructions?: boolean;
  emailRecalls?: boolean;
  emailReviewsAndSurveys?: boolean;
  emailSpecialsAndNewsletters?: boolean;
  emailTreatmentPlanFollowUps?: boolean;
  dmAppointmentReminders?: boolean;
  dmBirthdayAndHolidayCards?: boolean;
  dmExpiringInsuranceReminder?: boolean;
  dmPatientReactivation?: boolean;
  dmRecalls?: boolean;
  dmSpecialsAndNewsletters?: boolean;
  dmTreatmentPlanFollowUps?: boolean;
  textAppointmentReminders?: boolean;
  textPatientReactivation?: boolean;
  textRecalls?: boolean;
  textReviewsAndSurveys?: boolean;
  textSpecialsAndNewsletters?: boolean;
  textTreatmentPlanFollowUps?: boolean;
  pcAppointmentConfirmations?: boolean;
  language: PatientLanguage;
  firstReminderScheduleStatus: PatientCustomReminderSchedule;
  secondReminderScheduleStatus: PatientCustomReminderSchedule;
  saveDateReminderScheduleStatus: PatientCustomReminderSchedule;
  postCardReminderScheduleStatus: PatientCustomReminderSchedule;
  sameDayReminderScheduleStatus: PatientCustomReminderSchedule;
  familyCommunicationReceiverId?: number;
}

export enum PatientLanguage {
  English = 'English',
  Spanish = 'Spanish'
}

export enum PatientCustomReminderSchedule {
  Global = 'Global',
  On = 'On',
  Off = 'Off'
}
